import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { useSubscription } from '../../contexts/SubscriptionContext';

const UpgradeModal = ({ isOpen, onClose, feature, limit, current }) => {
  const { upgradeToProCheckout } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      await upgradeToProCheckout();
    } catch (error) {
      console.error('Upgrade error:', error);
      setError(error?.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const getFeatureDetails = () => {
    switch (feature) {
      case 'accounts':
        return {
          title: 'Account Limit Reached',
          description: `You've reached the Free plan limit of ${limit} accounts. Upgrade to Pro for unlimited accounts.`,
          icon: 'Wallet'
        };
      case 'recurring':
        return {
          title: 'Recurring Transaction Limit Reached',
          description: `You've reached the Free plan limit of ${limit} recurring transaction. Upgrade to Pro for unlimited recurring transactions.`,
          icon: 'Repeat'
        };
      case 'budgets':
        return {
          title: 'Budget Limit Reached',
          description: `You've reached the Free plan limit of ${limit} budget. Upgrade to Pro for unlimited budgets.`,
          icon: 'PieChart'
        };
      case 'alerts':
        return {
          title: 'Alert Limit Reached',
          description: `You've reached the Free plan limit of ${limit} alert. Upgrade to Pro for unlimited alerts.`,
          icon: 'Bell'
        };
      case 'reports':
        return {
          title: 'Pro Feature: Financial Reports',
          description: 'Financial reports are available exclusively for Pro members. Upgrade to access detailed reports and exports.',
          icon: 'FileText'
        };
      case 'transfers':
        return {
          title: 'Pro Feature: Transfers',
          description: 'Transfer transactions between accounts are available exclusively for Pro members.',
          icon: 'ArrowLeftRight'
        };
      case 'split':
        return {
          title: 'Pro Feature: Split Transactions',
          description: 'Split transactions across multiple categories are available exclusively for Pro members.',
          icon: 'Split'
        };
      default:
        return {
          title: 'Upgrade to Pro',
          description: 'This feature is available exclusively for Pro members.',
          icon: 'Crown'
        };
    }
  };

  const details = getFeatureDetails();

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
        <div className="bg-card rounded-lg shadow-elevation-3 max-w-md w-full mx-auto animate-scale-in">
          {/* Header */}
          <div className="p-24 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-12">
                <div className="w-48 h-48 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={details?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                    {details?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {details?.description}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>

          {/* Pro Features List */}
          <div className="p-24 bg-muted/30">
            <div className="flex items-center gap-8 mb-16">
              <Icon name="Crown" size={20} color="var(--color-primary)" />
              <h4 className="font-semibold text-foreground">Pro Plan Includes:</h4>
            </div>
            <ul className="space-y-12 text-sm text-muted-foreground">
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Unlimited accounts of all types</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Unlimited recurring transactions</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Unlimited budgets & alerts</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Transfers & split transactions</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Financial reports & exports</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={16} className="mt-2 flex-shrink-0 text-primary" />
                <span>Category rules automation</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="p-24 border-t border-border">
            {error && (
              <div className="mb-16 p-12 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-8">
                <Icon name="AlertCircle" size={16} className="text-destructive mt-2 flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
            <div className="flex gap-12">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleUpgrade}
                className="flex-1"
                iconName="Zap"
                iconPosition="left"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Upgrade to Pro'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeModal;